import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { getCoreRowModel } from '@tanstack/react-table';
import { Box, Paper, Typography } from '@mui/material';

// Hooks and services
import useConfirmDialog from '../../../../hooks/useConfirmDialog';
import { useNotifyMessage } from '../../../../hooks/useNotifyMessage';
import { Storage } from '../../../../utils/Storage';
import makePolicyColumn from './makePolicyColumn';
import { deletePolicy, exportPolicies, getPolicies } from '../../../../services/policy.service';

// Constants
import { CONFIRM_DIALOG_POLICY_DELETE, SearchBarFilterTypes } from '../../../../constants/Constants';

// Components
import { Table, useStandardTable as useTable } from '../../../common/table';
import SearchBar from '../../../common/SearchBar/SearchBar';
import PolicyForm from './policyForm';

// Types
import { Policy } from '../../../../types/Policy';

export default function Policies() {
  const navigateTo = useNavigate();
  const { notifyMessage } = useNotifyMessage();

  const [paramFilter, setParamFilter] = useState<any>({});
  const forceReload = () => setParamFilter({ ...paramFilter, changedAt: new Date() });

  const managePolicyPermissions = (id) => {
    navigateTo('/policies/' + id);
  };

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.POLICIES);
    if (filterState) {
      setParamFilter({
        filters: filterState.filters,
      });
    }
  };

  useEffect(() => {
    intialDefaultFilters();
  }, []);

  const columns = useMemo(() => makePolicyColumn(managePolicyPermissions, (policy: Policy) => handleDeletePolicy(policy)), []);

  const table = useTable<any>({
    columns,
    accessKey: 'id',
    fetchAsync: getPolicies,
    exportAsync: exportPolicies,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any) => {
    setParamFilter({
      filters: filters,
    });
  };

  const { renderDialog, confirm } = useConfirmDialog(CONFIRM_DIALOG_POLICY_DELETE);

  const handleDeletePolicy = useCallback(
    async (policy: Policy) => {
      const { accepted } = await confirm({}); // Open confirmation dialog
      if (!accepted) return;

      // If confirmed, try deleting role
      try {
        await deletePolicy(policy.id);
        notifyMessage('Deleted policy', 'success');
        forceReload();
      } catch (err: any) {
        notifyMessage(err.response.data ?? 'Failed to delete policy', 'error');
      }
    },
    [confirm],
  );

  return (
    <>
      {renderDialog()}

      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          rowGap: 1,
          columnGap: 1.5,
          mb: 1.5,
          px: { xs: 0.5, sm: 0.75 },
        }}
      >
        <Typography variant='h5' sx={{ m: 0, fontWeight: 700, color: '#1f2d63' }}>
          Policies
        </Typography>
        <Box>
          <PolicyForm />
        </Box>
      </Box>
      <React.Fragment>
        <SearchBar pageKey={SearchBarFilterTypes.POLICIES} onSearch={onSearchChange} columns={columns} allowSearchAll={true} />
        <Paper>
          <Table {...table} showSearchBar={false} showColumnToggler={false} showAction={true} getCoreRowModel={getCoreRowModel()} />
        </Paper>
      </React.Fragment>
    </>
  );
}
